import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Refresh,
  AttachFile,
  Message,
  Close,
  FiberManualRecord,
  InsertDriveFile,
  Send,
} from "@mui/icons-material";

// Mock user data - would be fetched from auth context in production
const MOCK_USER = {
  id: 101,
  name: "John Doe",
  avatar: "",
  email: "john.doe@example.com",
};

// Mock tickets data - would be fetched from API in production
const MOCK_TICKETS = [
  {
    id: 1,
    subject: "API connection issues",
    description: "I cannot connect my trading account to the platform.",
    status: "open",
    priority: "high",
    category: "technical",
    created: "2025-05-15T10:30:00",
    updated: "2025-05-16T09:15:00",
    messages: [
      {
        id: 1,
        sender: { id: 101, name: "John Doe", avatar: "", type: "user" },
        content:
          "I've been trying to connect my trading account but keep getting error code 403.",
        timestamp: "2025-05-15T10:30:00",
        attachments: [],
      },
      {
        id: 2,
        sender: { id: 1, name: "Admin", avatar: "", type: "admin" },
        content:
          "Could you please provide your account ID and the exact error message you're seeing?",
        timestamp: "2025-05-15T11:45:00",
        attachments: [],
      },
      {
        id: 3,
        sender: { id: 101, name: "John Doe", avatar: "", type: "user" },
        content:
          'My account ID is TD-58493. The error says "API key validation failed".',
        timestamp: "2025-05-16T09:15:00",
        attachments: [],
      },
    ],
  },
  {
    id: 2,
    subject: "Account verification",
    description: "My account is pending verification for over a week.",
    status: "in-progress",
    priority: "medium",
    category: "account",
    created: "2025-05-12T14:20:00",
    updated: "2025-05-13T10:45:00",
    messages: [
      {
        id: 1,
        sender: { id: 101, name: "John Doe", avatar: "", type: "user" },
        content:
          "I submitted my verification documents over a week ago but my account is still showing as unverified.",
        timestamp: "2025-05-12T14:20:00",
        attachments: [],
      },
      {
        id: 2,
        sender: { id: 1, name: "Admin", avatar: "", type: "admin" },
        content:
          "Thank you for your patience. Our verification team is currently experiencing higher than normal volumes. We will prioritize your verification and update you within 48 hours.",
        timestamp: "2025-05-13T10:45:00",
        attachments: [],
      },
    ],
  },
];

// Status colors for visual indicators
const STATUS_COLORS = {
  open: "#f44336", // Red
  "in-progress": "#ff9800", // Orange
  pending: "#2196f3", // Blue
  resolved: "#4caf50", // Green
  closed: "#9e9e9e", // Grey
};

const UserTickets = () => {
  const [tickets, setTickets] = useState(MOCK_TICKETS);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [newTicket, setNewTicket] = useState({
    subject: "",
    description: "",
    category: "technical",
    priority: "medium",
    attachments: [],
  });
  const [loading, setLoading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now()); // For resetting file input
  const [attachments, setAttachments] = useState([]);

  const handleTicketSelect = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleReplyChange = (e) => {
    setReplyText(e.target.value);
  };

  const handleNewTicketChange = (e) => {
    setNewTicket({
      ...newTicket,
      [e.target.name]: e.target.value,
    });
  };

  const handleSendReply = () => {
    if (!replyText.trim()) return;

    // Simulate API call
    setLoading(true);

    setTimeout(() => {
      const newMessage = {
        id: selectedTicket.messages.length + 1,
        sender: { ...MOCK_USER, type: "user" },
        content: replyText,
        timestamp: new Date().toISOString(),
        attachments: [...attachments],
      };

      const updatedTickets = tickets.map((ticket) => {
        if (ticket.id === selectedTicket.id) {
          return {
            ...ticket,
            messages: [...ticket.messages, newMessage],
            updated: new Date().toISOString(),
          };
        }
        return ticket;
      });

      setTickets(updatedTickets);
      setSelectedTicket({
        ...selectedTicket,
        messages: [...selectedTicket.messages, newMessage],
        updated: new Date().toISOString(),
      });

      setReplyText("");
      setAttachments([]);
      setFileInputKey(Date.now());
      setLoading(false);
    }, 1000);
  };

  const handleSubmitNewTicket = () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) return;

    // Simulate API call
    setLoading(true);

    setTimeout(() => {
      const newTicketObj = {
        id: Math.max(...tickets.map((t) => t.id), 0) + 1,
        ...newTicket,
        status: "open",
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        messages: [
          {
            id: 1,
            sender: { ...MOCK_USER, type: "user" },
            content: newTicket.description,
            timestamp: new Date().toISOString(),
            attachments: [...attachments],
          },
        ],
      };

      setTickets([newTicketObj, ...tickets]);
      setNewTicketOpen(false);
      setNewTicket({
        subject: "",
        description: "",
        category: "technical",
        priority: "medium",
        attachments: [],
      });
      setAttachments([]);
      setFileInputKey(Date.now());
      setLoading(false);
    }, 1500);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Simulate file upload
    const newAttachments = files.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file), // In a real app, this would be the URL from your file server
    }));

    setAttachments([...attachments, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusText = (status) => {
    return status.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Support Tickets</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setNewTicketOpen(true)}
        >
          Create Ticket
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left side - Ticket List */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2,
              height: "75vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">My Tickets</Typography>
              <IconButton size="small">
                <Refresh />
              </IconButton>
            </Box>

            {/* Ticket List */}
            <List sx={{ overflow: "auto", flexGrow: 1 }}>
              {tickets.length > 0 ? (
                tickets.map((ticket) => (
                  <React.Fragment key={ticket.id}>
                    <ListItem
                      alignItems="flex-start"
                      component="div"
                      selected={
                        selectedTicket && selectedTicket.id === ticket.id
                      }
                      onClick={() => handleTicketSelect(ticket)}
                      sx={{
                        borderLeft: `4px solid ${STATUS_COLORS[ticket.status]}`,
                        "&:hover": { backgroundColor: "action.hover" },
                        "&.Mui-selected": {
                          backgroundColor: "action.selected",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              noWrap
                              sx={{ maxWidth: "180px" }}
                            >
                              {ticket.subject}
                            </Typography>
                            <FiberManualRecord
                              fontSize="small"
                              sx={{ color: STATUS_COLORS[ticket.status] }}
                            />
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Box component="span">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                display="block"
                              >
                                Created: {formatDate(ticket.created)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                display="block"
                              >
                                Updated: {formatDate(ticket.updated)}
                              </Typography>
                            </Box>
                            {/* Moved chips outside of Typography to avoid DOM nesting warning */}
                            <Box
                              sx={{
                                mt: 0.5,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              <Chip
                                size="small"
                                label={getStatusText(ticket.status)}
                                sx={{
                                  backgroundColor: `${STATUS_COLORS[ticket.status]}20`,
                                  color: STATUS_COLORS[ticket.status],
                                  textTransform: "capitalize",
                                }}
                              />
                              <Chip
                                size="small"
                                label={ticket.category}
                                sx={{ textTransform: "capitalize" }}
                              />
                            </Box>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              ) : (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    No tickets yet
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Create a new support ticket to get help
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    sx={{ mt: 2 }}
                    onClick={() => setNewTicketOpen(true)}
                  >
                    Create Ticket
                  </Button>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Right side - Ticket Details */}
        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              p: 2,
              height: "75vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {selectedTicket ? (
              <React.Fragment>
                {/* Ticket Header */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6">{selectedTicket.subject}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={getStatusText(selectedTicket.status)}
                      sx={{
                        backgroundColor: `${STATUS_COLORS[selectedTicket.status]}20`,
                        color: STATUS_COLORS[selectedTicket.status],
                        textTransform: "capitalize",
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Ticket #{selectedTicket.id} • Created on{" "}
                      {formatDate(selectedTicket.created)}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {/* Message Thread */}
                <Box sx={{ flexGrow: 1, overflow: "auto", mb: 2 }}>
                  {selectedTicket.messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: (theme) =>
                          message.sender.type === "admin"
                            ? theme.palette.primary.dark
                            : theme.palette.mode === "dark"
                              ? theme.palette.grey[800]
                              : theme.palette.grey[100],
                        color: (theme) =>
                          message.sender.type === "admin"
                            ? theme.palette.primary.contrastText
                            : theme.palette.getContrastText(
                                theme.palette.mode === "dark"
                                  ? theme.palette.grey[800]
                                  : theme.palette.grey[100],
                              ),
                        borderRadius: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                          {message.sender.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {message.sender.name}
                            {message.sender.type === "admin" && (
                              <Chip
                                size="small"
                                label="Support"
                                sx={{
                                  ml: 1,
                                  backgroundColor: "success.main",
                                  color: "white",
                                }}
                              />
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "inherit", opacity: 0.8 }}
                          >
                            {formatDate(message.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2">{message.content}</Typography>
                      {message.attachments &&
                        message.attachments.length > 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {message.attachments.map((attachment, index) => (
                              <Chip
                                key={index}
                                icon={<InsertDriveFile />}
                                label={attachment.name}
                                variant="outlined"
                                size="small"
                                sx={{ bgcolor: "background.default" }}
                                onClick={() => window.open(attachment.url)}
                              />
                            ))}
                          </Box>
                        )}
                    </Box>
                  ))}
                </Box>

                {/* Reply Form */}
                <Box
                  sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider" }}
                >
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your reply here..."
                    variant="outlined"
                    value={replyText}
                    onChange={handleReplyChange}
                    disabled={loading || selectedTicket.status === "closed"}
                  />

                  {/* Attachments */}
                  {attachments.length > 0 && (
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                      }}
                    >
                      {attachments.map((file, index) => (
                        <Chip
                          key={index}
                          icon={<InsertDriveFile />}
                          label={file.name}
                          onDelete={() => removeAttachment(index)}
                          variant="outlined"
                          size="small"
                          sx={{ bgcolor: "background.default" }}
                        />
                      ))}
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 2,
                    }}
                  >
                    {/* Attachment button */}
                    <Button
                      component="label"
                      startIcon={<AttachFile />}
                      variant="outlined"
                      disabled={loading || selectedTicket.status === "closed"}
                    >
                      Attach Files
                      <input
                        key={fileInputKey}
                        type="file"
                        hidden
                        multiple
                        onChange={handleFileChange}
                      />
                    </Button>

                    {/* Reply button */}
                    <Button
                      variant="contained"
                      endIcon={
                        loading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <Send />
                        )
                      }
                      disabled={
                        !replyText.trim() ||
                        loading ||
                        selectedTicket.status === "closed"
                      }
                      onClick={handleSendReply}
                    >
                      {loading ? "Sending..." : "Reply"}
                    </Button>
                  </Box>

                  {selectedTicket.status === "closed" && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mt: 1, textAlign: "center" }}
                    >
                      This ticket is closed. Please create a new ticket if you
                      need further assistance.
                    </Typography>
                  )}
                </Box>
              </React.Fragment>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Message
                  sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  Select a ticket or create a new one
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Our support team is ready to help you
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setNewTicketOpen(true)}
                >
                  Create New Ticket
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* New Ticket Dialog */}
      <Dialog
        open={newTicketOpen}
        onClose={() => setNewTicketOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        <DialogTitle>
          Create New Support Ticket
          <IconButton
            onClick={() => setNewTicketOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            disabled={loading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Subject"
            name="subject"
            value={newTicket.subject}
            onChange={handleNewTicketChange}
            margin="normal"
            required
            variant="outlined"
            placeholder="Brief description of the issue"
            disabled={loading}
          />

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={newTicket.category}
                  onChange={handleNewTicketChange}
                  label="Category"
                  disabled={loading}
                >
                  <MenuItem value="technical">Technical Issue</MenuItem>
                  <MenuItem value="billing">Billing Question</MenuItem>
                  <MenuItem value="account">Account Management</MenuItem>
                  <MenuItem value="trading">Trading Problem</MenuItem>
                  <MenuItem value="feature">Feature Request</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={newTicket.priority}
                  onChange={handleNewTicketChange}
                  label="Priority"
                  disabled={loading}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Description"
            name="description"
            value={newTicket.description}
            onChange={handleNewTicketChange}
            margin="normal"
            required
            variant="outlined"
            placeholder="Please provide as much detail as possible about your issue"
            disabled={loading}
          />

          {/* Attachment Button */}
          <Button
            component="label"
            startIcon={<AttachFile />}
            variant="outlined"
            sx={{ mt: 2 }}
            disabled={loading}
          >
            Attach Files
            <input
              key={fileInputKey}
              type="file"
              hidden
              multiple
              onChange={handleFileChange}
            />
          </Button>

          {/* Attachments */}
          {attachments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Attachments:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {attachments.map((file, index) => (
                  <Chip
                    key={index}
                    icon={<InsertDriveFile />}
                    label={file.name}
                    onDelete={() => removeAttachment(index)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setNewTicketOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitNewTicket}
            disabled={
              !newTicket.subject.trim() ||
              !newTicket.description.trim() ||
              loading
            }
            startIcon={
              loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                    color: "primary.main",
                  }}
                />
              )
            }
          >
            {loading ? "Submitting..." : "Submit Ticket"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserTickets;
